import React from 'react';
import {
  useSalesforceLeadRecordTypeByRecordTypeIdQuery,
  useUpdateSalesforceLeadLeadpadFieldMutation,
  SalesforceLeadRecordType,
  SalesforceLeadField,
  SalesforceLeadLeadpadField,
} from 'api';
import {Listbox, Transition} from '@headlessui/react';
import {ChevronUpDownIcon} from '@heroicons/react/20/solid';

interface Props {
  salesforceLeadLeadpadField: SalesforceLeadLeadpadField;
  salesforceLeadRecordType: SalesforceLeadRecordType;
}

export default ({
  salesforceLeadLeadpadField,
  salesforceLeadRecordType,
}: Props) => {
  const {
    data: {
      salesforceLeadRecordTypeByRecordTypeId: {salesforceLeadFields = []} = {},
    } = {},
  } = useSalesforceLeadRecordTypeByRecordTypeIdQuery({
    variables: {
      salesforceLeadRecordTypeId: salesforceLeadRecordType?.id,
    },
    skip: !salesforceLeadRecordType,
  });

  const salesforceLeadField = salesforceLeadLeadpadField?.salesforceLeadField;

  const fieldOptions = salesforceLeadFields
    .filter(
      (field: SalesforceLeadField) =>
        field.fieldType === salesforceLeadLeadpadField.fieldType,
    )
    .filter((field: SalesforceLeadField) => !field.salesforceLeadLeadpadField)
    .filter((field: SalesforceLeadField) => field.updateable);

  const [update] = useUpdateSalesforceLeadLeadpadFieldMutation({
    refetchQueries: ['salesforceLeadRecordTypeByRecordTypeId'],
  });

  return (
    <div className="text-foreground-dark w-96 px-6 text-base">
      <Listbox
        onChange={(leadField: SalesforceLeadField) => {
          update({
            variables: {
              id: salesforceLeadLeadpadField?.id,
              leadFieldId: leadField?.id || null,
            },
          });
        }}>
        <div className="relative mt-1">
          <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left shadow-md focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm min-h-[37.59px]">
            <span className="block truncate" />
            {salesforceLeadField?.label || ''}
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>
          <Transition
            as={React.Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <Listbox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-20 ">
              <Listbox.Option
                value={null}
                className={({active}) =>
                  `min-h-[37.59px]  relative cursor-pointer select-none p-3 ${active ? 'bg-gray-100' : 'bg-white'}`
                }></Listbox.Option>
              {fieldOptions.map((field) => (
                <Listbox.Option
                  key={field.id}
                  className={({active}) =>
                    `min-h-[37.59px]  relative cursor-pointer select-none p-3 ${
                      active ? 'bg-gray-100' : 'bg-white'
                    }`
                  }
                  value={field}>
                  {({selected}) => (
                    <>
                      <span
                        className={`block truncate ${
                          selected ? 'font-medium' : 'font-normal'
                        }`}>
                        {field.label}
                      </span>
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
};
